<template>
    <div class="map-view">
        <div id="map" ref="map"></div>
        <!-- No user location notification -->
        <div v-if="showNoLocationBanner" class="no-location-banner">
            <div class="banner-content">
                <span class="banner-icon">📍</span>
                <span class="banner-text">Brak lokalizacji użytkownika - wyświetlana trasa wydarzenia</span>
            </div>
        </div>
        <!-- Settings button -->
        <button class="settings-btn" @click="toggleSettings" title="Ustawienia mapy">
            <span class="settings-icon">⚙️</span>
        </button>
        <!-- Center on runner button -->
        <button class="center-btn" @click="centerOnRunner" title="Wycentruj na biegaczu" v-if="latestPosition">
            <span class="center-icon">🎯</span>
        </button>
        <!-- Settings panel -->
        <MapSettings 
            :is-visible="showSettings"
            :settings="mapSettings"
            @close="showSettings = false"
            @update-settings="updateMapSettings"
        />
    </div>
</template>
<script>
import { onMounted, ref, watch, defineAsyncComponent } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { haversineDistance } from '../utils/gpsUtils.js';
import { debounce, throttle, SimpleCache, createBearingCacheKey } from '../utils/performanceUtils.js';

// Lazy load MapSettings component
const MapSettings = defineAsyncComponent(() => import('./MapSettings.vue'));

export default {
    components: {
        MapSettings
    },
    props: {
        tracks: {
            type: Array,
            default: () => []
        },
        latestPosition: {
            type: Object,
            default: null
        },
        eventRoute: {
            type: Array,
            default: () => []
        },
        eventMarkers: {
            type: Array,
            default: () => []
        },
        runnerData: {
            type: Object,
            default: null
        }
    },
    setup(props) {
        const map = ref(null);
        let leafletMapInstance = null; // Store the actual Leaflet map outside Vue reactivity
        const trackPolyline = ref(null);
        const currentPositionMarker = ref(null);
        const eventRoutePolyline = ref(null);
        const eventMarkersGroup = ref(null);
        const distanceMarkersGroup = ref(null);
        const showNoLocationBanner = ref(false);
        let isZooming = false; // Track zoom state to prevent layer updates during zoom animations
        
        // Performance optimizations
        const bearingCache = new SimpleCache(50);
        let lastTrackLength = 0;
        let lastPositionTime = null;
        let lastCenterTime = 0;
        let userHasInteracted = false;
        let lastZoomLevel = 13; // Track zoom level to detect changes
        
        // Settings management
        const showSettings = ref(false);
        const mapSettings = ref({
            showUserTrack: false,
            showEventRoute: true,
            showDistanceMarkers: true,
            showEventMarkers: true,
            autoFollowRunner: true
        });
        
        // Load settings from localStorage on mount
        const loadSettings = () => {
            try {
                const saved = localStorage.getItem('mapSettings');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    mapSettings.value = { ...mapSettings.value, ...parsed };
                }
            } catch (error) {
                console.warn('Error loading map settings:', error);
            }
        };
        
        // Save settings to localStorage
        const saveSettings = (settings) => {
            try {
                localStorage.setItem('mapSettings', JSON.stringify(settings));
            } catch (error) {
                console.warn('Error saving map settings:', error);
            }
        };
        
        const toggleSettings = () => {
            showSettings.value = !showSettings.value;
        };
        
        const updateMapSettings = (newSettings) => {
            mapSettings.value = { ...newSettings };
            saveSettings(newSettings);
            // Update map layers based on new settings
            updateLayerVisibility();
        };
        
        const updateLayerVisibility = () => {
            if (!leafletMapInstance) return;

            // Toggle user track visibility
            if (trackPolyline.value) {
                if (mapSettings.value.showUserTrack) {
                    if (trackPolyline.value._map !== leafletMapInstance) {
                        leafletMapInstance.addLayer(trackPolyline.value);
                    }
                } else {
                    if (trackPolyline.value._map === leafletMapInstance) {
                        leafletMapInstance.removeLayer(trackPolyline.value);
                    }
                }
            }

            // Toggle event route visibility
            if (eventRoutePolyline.value) {
                if (mapSettings.value.showEventRoute) {
                    if (eventRoutePolyline.value._map !== leafletMapInstance) {
                        leafletMapInstance.addLayer(eventRoutePolyline.value);
                    }
                } else {
                    if (eventRoutePolyline.value._map === leafletMapInstance) {
                        leafletMapInstance.removeLayer(eventRoutePolyline.value);
                    }
                }
            }

            // Toggle distance markers visibility
            if (distanceMarkersGroup.value) {
                if (mapSettings.value.showDistanceMarkers) {
                    if (distanceMarkersGroup.value._map !== leafletMapInstance) {
                        leafletMapInstance.addLayer(distanceMarkersGroup.value);
                    }
                } else {
                    if (distanceMarkersGroup.value._map === leafletMapInstance) {
                        leafletMapInstance.removeLayer(distanceMarkersGroup.value);
                    }
                }
            }

            // Toggle event markers visibility
            if (eventMarkersGroup.value) {
                if (mapSettings.value.showEventMarkers) {
                    if (eventMarkersGroup.value._map !== leafletMapInstance) {
                        leafletMapInstance.addLayer(eventMarkersGroup.value);
                    }
                } else {
                    if (eventMarkersGroup.value._map === leafletMapInstance) {
                        leafletMapInstance.removeLayer(eventMarkersGroup.value);
                    }
                }
            }
        };
        
        onMounted(() => {
            // Load settings from localStorage
            loadSettings();
            
            // Initialize map with default view
            leafletMapInstance = L.map('map').setView([50.7645, 16.2813], 13);
            
            // Set the ref for template access
            map.value = leafletMapInstance;
            
            // Initialize last zoom level
            lastZoomLevel = leafletMapInstance.getZoom();
            
            // Add zoom event listeners to track zoom state
            leafletMapInstance.on('zoomstart', () => {
                console.log('Zoom started');
                isZooming = true;
                userHasInteracted = true;
                // Close any open popups to prevent interference with zoom animation
                leafletMapInstance.closePopup();
            });
            
            leafletMapInstance.on('zoomend', () => {
                console.log('Zoom ended');
                isZooming = false;
                // Store old zoom level before updating for interval comparison
                const oldZoomLevel = lastZoomLevel;
                const newZoomLevel = leafletMapInstance.getZoom();
                lastZoomLevel = newZoomLevel;
                // Increase delay and debounce updates after zoom animation completes
                debouncedUpdateAfterZoom(oldZoomLevel, newZoomLevel);
                console.log('Map zoom level:', lastZoomLevel);
            });
            
            // Track user interaction to avoid auto-centering during manual navigation
            leafletMapInstance.on('drag', () => {
                userHasInteracted = true;
            });
            
            // Add OpenStreetMap tiles
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; OpenStreetMap contributors'
            }).addTo(leafletMapInstance);
            
            // Initialize tracking data if available
            updateTrackingData();
            // Initialize event data if available
            updateEventData();
        });
        
        // Update map when tracking data changes - remove deep watching to improve performance
        watch([() => props.tracks, () => props.latestPosition], () => {
            // Don't update layers during zoom animations to prevent crashes
            if (!isZooming) {
                debouncedUpdateTrackingData();
            }
        }); // Removed { deep: true } for better performance
        
        // Update map when event data changes - only watch for array/object reference changes
        watch([() => props.eventRoute, () => props.eventMarkers], () => {
            // Don't update layers during zoom animations to prevent crashes  
            if (leafletMapInstance && !isZooming) {
                debouncedUpdateEventData();
            }
        }); // Removed { deep: true } for better performance
        
        // Create debounced update functions to prevent excessive updates
        const debouncedUpdateTrackingData = debounce(() => {
            if (!isZooming) {
                console.log('Updating tracking data...');
                updateTrackingData();
            }
        }, 100);
        
        const debouncedUpdateEventData = debounce(() => {
            if (!isZooming) {
                console.log('Updating event data...');
                updateEventData();
            }
        }, 100);
        
        const debouncedUpdateAfterZoom = debounce((oldZoom, newZoom) => {
            if (!isZooming) { // Double-check zoom state
                console.log('Updating all data after zoom...');
                
                // Check if we need to update distance markers after zoom change
                if (leafletMapInstance && 
                    mapSettings.value.showDistanceMarkers && 
                    props.eventRoute && 
                    props.eventRoute.length > 1) {
                    
                    // Only regenerate markers if the marker interval actually changed
                    const oldInterval = getMarkerInterval(oldZoom);
                    const newInterval = getMarkerInterval(newZoom);
                    
                    if (oldInterval !== newInterval) {
                        console.log(`Marker interval changed from ${oldInterval}km to ${newInterval}km, updating distance markers...`);
                        const routePoints = props.eventRoute.map(point => [point.latitude, point.longitude]);
                        createDistanceMarkers(routePoints);
                    } else {
                        console.log(`Marker interval unchanged (${newInterval}km), skipping distance marker update`);
                    }
                }
            }
        }, 200); // Longer delay after zoom for better performance
        
        const updateTrackingData = () => {
            if (!leafletMapInstance || isZooming ) return;

            console.log('Attempting to update tracking data...');
            console.log('leafletMapInstance:', leafletMapInstance);
            console.log('mapHasLayer function:',typeof leafletMapInstance.hasLayer !== 'function' ? 'not a function' : 'is a function');
            
            // Performance optimization: check if tracking data actually changed
            const currentTrackLength = props.tracks ? props.tracks.length : 0;
            const currentPositionTime = props.latestPosition ? props.latestPosition.time : null;
            
            const trackDataChanged = currentTrackLength !== lastTrackLength;
            const positionChanged = currentPositionTime !== lastPositionTime;
            
            // Only update if data actually changed
            if (!trackDataChanged && !positionChanged) {
                return;
            }
            
            lastTrackLength = currentTrackLength;
            lastPositionTime = currentPositionTime;
            
            // Clear existing tracking elements
            try {
                if (trackPolyline.value && trackPolyline.value._map === leafletMapInstance) {
                    leafletMapInstance.removeLayer(trackPolyline.value);
                    trackPolyline.value = null;
                }
            } catch (error) {
                console.warn('Error clearing track polyline:', error);
                trackPolyline.value = null;
            }
            
            // Only clear current position marker if we don't have position data
            if (!props.latestPosition) {
                try {
                    if (currentPositionMarker.value && currentPositionMarker.value._map === leafletMapInstance) {
                        leafletMapInstance.removeLayer(currentPositionMarker.value);
                        currentPositionMarker.value = null;
                    }
                } catch (error) {
                    console.warn('Error clearing current position marker:', error);
                    currentPositionMarker.value = null;
                }
            }
            
            // Add track polyline if we have tracking points
            if (props.tracks && props.tracks.length > 1) {
                const trackPoints = props.tracks.map(track => [track.latitude, track.longitude]);
                trackPolyline.value = L.polyline(trackPoints, {
                    color: '#e74c3c',
                    weight: 4,
                    opacity: 0.8,
                    smoothFactor: 1
                });
                if (mapSettings.value.showUserTrack) {
                    trackPolyline.value.addTo(leafletMapInstance);
                }
            }
            
            // Add current position marker
            if (props.latestPosition) {
                const { latitude, longitude, speed, altitude, time } = props.latestPosition;
                
                // Check if position is inactive (older than 15 minutes)
                const isInactive = time ? isPositionInactive(time) : false;
                
                // Calculate direction arrow
                let directionArrowHtml = '';
                
                // First try to use direction parameter from track data (0-360 degrees)
                if (props.tracks && props.tracks.length > 0) {
                    const latestTrack = props.tracks[props.tracks.length - 1];
                    if (latestTrack.direction !== undefined && latestTrack.direction !== null) {
                        // Use direction parameter directly (0-360 degrees)
                        directionArrowHtml = `<div class="direction-arrow${isInactive ? ' inactive' : ''}" style="transform: rotate(${latestTrack.direction}deg);"></div>`;
                    }
                }
                
                // Fallback to bearing calculation if direction is not available and we have track history
                if (!directionArrowHtml && props.tracks && props.tracks.length >= 2) {
                    // Get the last two tracking points to calculate direction
                    const currentPoint = props.tracks[props.tracks.length - 1];
                    const previousPoint = props.tracks[props.tracks.length - 2];
                    
                    if (currentPoint && previousPoint) {
                        const bearing = calculateBearing(
                            previousPoint.latitude, 
                            previousPoint.longitude,
                            currentPoint.latitude, 
                            currentPoint.longitude
                        );
                        
                        directionArrowHtml = `<div class="direction-arrow${isInactive ? ' inactive' : ''}" style="transform: rotate(${bearing}deg);"></div>`;
                    }
                }
                
                // Create custom icon for current position with conditional styling for inactive state
                const markerClass = isInactive ? 'current-position-marker inactive' : 'current-position-marker';
                
                // Get user name for label
                const userName = props.runnerData ? `${props.runnerData.firstName || ''} ${props.runnerData.lastName || ''}`.trim() : '';
                const userNameLabel = userName ? `<div class="user-name-label">${userName}</div>` : '';
                
                const currentIcon = L.divIcon({
                    html: `
                        <div class="${markerClass}"></div>
                        ${directionArrowHtml}
                        ${userNameLabel}
                    `,
                    className: 'current-position-icon',
                    iconSize: [40, 40],
                    iconAnchor: [20, 20]
                });
                
                // Create popup with position details, including inactive status
                const statusText = isInactive ? '<strong style="color: #e67e22;">Status: Nieaktywny (>15 min)</strong><br>' : '';
                const popupContent = `
                    <div class="position-popup">
                        <strong>Aktualna pozycja</strong><br>
                        ${statusText}
                        ${speed ? `Prędkość: ${speed.toFixed(1)} km/h<br>` : ''}
                        ${altitude ? `Wysokość: ${Math.round(altitude)} m<br>` : ''}
                        ${time ? `Czas: ${formatTime(time)}` : ''}
                    </div>
                `;
                
                // Reuse existing marker if it exists, otherwise create a new one
                if (currentPositionMarker.value) {
                    // Update existing marker position, icon, and popup
                    currentPositionMarker.value.setLatLng([latitude, longitude]);
                    currentPositionMarker.value.setIcon(currentIcon);
                    currentPositionMarker.value.setPopupContent(popupContent);
                } else {
                    // Create new marker if none exists
                    currentPositionMarker.value = L.marker([latitude, longitude], {
                        icon: currentIcon
                    }).addTo(leafletMapInstance);
                    currentPositionMarker.value.bindPopup(popupContent);
                }
                
                // Only auto-center if auto-follow is enabled and user hasn't interacted recently
                const now = Date.now();
                const shouldCenter = mapSettings.value.autoFollowRunner && 
                                   (!userHasInteracted || (now - lastCenterTime) > 30000);
                
                if (shouldCenter && leafletMapInstance) {
                    leafletMapInstance.setView([latitude, longitude], 17);
                    showNoLocationBanner.value = false; // Hide banner when we have user location
                    lastCenterTime = now;
                    // Don't reset userHasInteracted flag - preserve user choice to explore map
                }
            } else {
                // No user location available - we'll show the banner when event route is displayed
                // The banner will be shown in updateEventData if event route is displayed
            }
        };
        
        const updateEventData = () => {
            // Don't update layers during zoom animations to prevent crashes
            if (!leafletMapInstance || isZooming) {
                return;
            }
            
            // Clear existing event elements
            try {
                if (eventRoutePolyline.value && eventRoutePolyline.value._map === leafletMapInstance) {
                    leafletMapInstance.removeLayer(eventRoutePolyline.value);
                    eventRoutePolyline.value = null;
                }
            } catch (error) {
                console.warn('Error clearing event route:', error);
                eventRoutePolyline.value = null;
            }
            
            try {
                if (eventMarkersGroup.value && eventMarkersGroup.value._map === leafletMapInstance) {
                    leafletMapInstance.removeLayer(eventMarkersGroup.value);
                    eventMarkersGroup.value = null;
                }
            } catch (error) {
                console.warn('Error clearing event markers:', error);
                eventMarkersGroup.value = null;
            }
            
            try {
                if (distanceMarkersGroup.value && distanceMarkersGroup.value._map === leafletMapInstance) {
                    leafletMapInstance.removeLayer(distanceMarkersGroup.value);
                    distanceMarkersGroup.value = null;
                }
            } catch (error) {
                console.warn('Error clearing distance markers:', error);
                distanceMarkersGroup.value = null;
            }
            
            // Add event route polyline if we have route data
            if (props.eventRoute && props.eventRoute.length > 1) {
                const routePoints = props.eventRoute.map(point => [point.latitude, point.longitude]);
                
                try {
                    eventRoutePolyline.value = L.polyline(routePoints, {
                        color: '#3498db',
                        weight: 6,
                        opacity: 0.7,
                        smoothFactor: 1
                    });
                    
                    // Only add to map if settings allow it
                    if (mapSettings.value.showEventRoute) {
                        eventRoutePolyline.value.addTo(leafletMapInstance);
                    }
                    
                    // Add distance markers along the route
                    createDistanceMarkers(routePoints);
                } catch (error) {
                    console.error('Error adding event route:', error);
                }
                
                // Fit map bounds to event route if no user location is available
                if (!props.latestPosition && eventRoutePolyline.value && leafletMapInstance) {
                    try {
                        leafletMapInstance.fitBounds(eventRoutePolyline.value.getBounds(), {
                            padding: [20, 20]
                        });
                        showNoLocationBanner.value = true; // Show banner when displaying route without user location
                    } catch (error) {
                        console.warn('Error fitting bounds to event route:', error);
                    }
                } else {
                    // Hide banner if we have user location or no event route to show
                    showNoLocationBanner.value = false;
                }
            }
            
            // Add event markers
            if (props.eventMarkers && props.eventMarkers.length > 0) {
                eventMarkersGroup.value = L.layerGroup();
                
                props.eventMarkers.forEach(marker => {
                    if (marker.latitude && marker.longitude) {
                        // Create different icons based on marker type
                        const markerIcon = createEventMarkerIcon(marker.type);
                        
                        const leafletMarker = L.marker([marker.latitude, marker.longitude], {
                            icon: markerIcon
                        });
                        
                        // Add popup with marker details
                        const popupContent = `
                            <div class="event-marker-popup">
                                <strong>${marker.text || 'Punkt kontrolny'}</strong><br>
                                ${marker.sort ? `Kolejność: ${marker.sort}<br>` : ''}
                                Typ: ${getMarkerTypeText(marker.type)}
                            </div>
                        `;
                        
                        leafletMarker.bindPopup(popupContent);
                        eventMarkersGroup.value.addLayer(leafletMarker);
                    }
                });
                
                // Only add to map if settings allow it
                if (mapSettings.value.showEventMarkers) {
                    eventMarkersGroup.value.addTo(leafletMapInstance);
                }
            }
        };
        
        const createEventMarkerIcon = (type) => {
            // Different marker styles based on type
            let color = '#2c3e50'; // default
            let symbol = '●';
            
            switch (type) {
                case 1: // Start
                    color = '#27ae60';
                    symbol = '▶';
                    break;
                case 2: // Finish  
                    color = '#e74c3c';
                    symbol = '⏸';
                    break;
                case 3: // Checkpoint
                    color = '#f39c12';
                    symbol = '◆';
                    break;
                default:
                    color = '#3498db';
                    symbol = '●';
            }
            
            return L.divIcon({
                html: `<div class="event-marker" style="background-color: ${color};">${symbol}</div>`,
                className: 'event-marker-icon',
                iconSize: [24, 24],
                iconAnchor: [12, 12],
                popupAnchor: [0, -12]
            });
        };
        
        const getMarkerTypeText = (type) => {
            switch (type) {
                case 1: return 'Start';
                case 2: return 'Meta';
                case 3: return 'Punkt kontrolny';
                default: return 'Punkt';
            }
        };
        
        const formatTime = (timeString) => {
            try {
                const date = new Date(timeString);
                return date.toLocaleTimeString('pl-PL', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                });
            } catch (error) {
                return timeString;
            }
        };

        // Check if position is older than 15 minutes (inactive)
        const isPositionInactive = (timeString) => {
            try {
                const positionTime = new Date(timeString);
                const currentTime = new Date();
                const timeDifferenceMinutes = (currentTime - positionTime) / (1000 * 60);
                return timeDifferenceMinutes > 15;
            } catch (error) {
                console.warn('Error checking position activity:', error);
                return false; // Default to active if we can't determine
            }
        };

        // Calculate bearing (direction) between two GPS coordinates in degrees - with caching
        const calculateBearing = (lat1, lng1, lat2, lng2) => {
            const cacheKey = createBearingCacheKey(lat1, lng1, lat2, lng2);
            
            // Check cache first
            if (bearingCache.has(cacheKey)) {
                return bearingCache.get(cacheKey);
            }
            
            const toRadians = (degrees) => degrees * (Math.PI / 180);
            const toDegrees = (radians) => radians * (180 / Math.PI);

            const dLng = toRadians(lng2 - lng1);
            const lat1Rad = toRadians(lat1);
            const lat2Rad = toRadians(lat2);

            const y = Math.sin(dLng) * Math.cos(lat2Rad);
            const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - 
                      Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);

            let bearing = toDegrees(Math.atan2(y, x));
            bearing = (bearing + 360) % 360; // Normalize to 0-360 degrees
            
            // Cache the result
            bearingCache.set(cacheKey, bearing);
            
            return bearing;
        };
        
        // Determine marker interval based on zoom level
        const getMarkerInterval = (zoomLevel) => {
            if (zoomLevel >= 17) return 1;    // Every 1km for high zoom
            if (zoomLevel >= 15) return 2;    // Every 2km for medium-high zoom
            if (zoomLevel >= 13) return 5;    // Every 5km for medium zoom
            if (zoomLevel >= 10) return 10;   // Every 10km for low zoom
            if (zoomLevel >= 8) return 50;    // Every 50km for very low zoom
            return 100;                       // Every 100km for lowest zoom
        };

        // Create distance markers along the route - throttled to improve performance
        const createDistanceMarkers = throttle((routePoints) => {
            if (!routePoints || routePoints.length < 2) return;
            
            // Clean up existing distance markers if they exist
            if (distanceMarkersGroup.value && leafletMapInstance) {
                if (distanceMarkersGroup.value._map === leafletMapInstance) {
                    leafletMapInstance.removeLayer(distanceMarkersGroup.value);
                }
            }
            
            // Get current zoom level to determine marker interval
            const currentZoom = leafletMapInstance ? leafletMapInstance.getZoom() : 13;
            const markerInterval = getMarkerInterval(currentZoom);
            
            distanceMarkersGroup.value = L.layerGroup();
            let totalDistance = 0;
            let nextMarkerTarget = markerInterval; // Distance for next marker
            
            // Process all markers at once for now (can be optimized further if needed)
            for (let i = 1; i < routePoints.length; i++) {
                const segmentDistance = haversineDistance(
                    routePoints[i - 1][0],
                    routePoints[i - 1][1],
                    routePoints[i][0],
                    routePoints[i][1]
                );
                const segmentStartDistance = totalDistance;
                totalDistance += segmentDistance;
                
                // Check if we crossed one or more marker targets in this segment
                while (nextMarkerTarget <= totalDistance) {
                    // Calculate how far into this segment the marker should be placed
                    const distanceIntoSegment = nextMarkerTarget - segmentStartDistance;
                    const segmentRatio = distanceIntoSegment / segmentDistance;
                    
                    // Interpolate position along the current segment
                    const lat1 = routePoints[i - 1][0];
                    const lng1 = routePoints[i - 1][1];
                    const lat2 = routePoints[i][0];
                    const lng2 = routePoints[i][1];
                    
                    const markerLat = lat1 + (lat2 - lat1) * segmentRatio;
                    const markerLng = lng1 + (lng2 - lng1) * segmentRatio;
                    
                    // Create custom icon for distance marker
                    const distanceIcon = L.divIcon({
                        html: `<div class="distance-marker">${Math.round(nextMarkerTarget)}</div>`,
                        className: 'distance-marker-container',
                        iconSize: [30, 15],
                        iconAnchor: [15, 7]
                    });
                    
                    const marker = L.marker([markerLat, markerLng], { icon: distanceIcon });
                    distanceMarkersGroup.value.addLayer(marker);
                    
                    nextMarkerTarget += markerInterval;
                }
            }
            
            if (distanceMarkersGroup.value && leafletMapInstance) {
                // Only add to map if settings allow it
                if (mapSettings.value.showDistanceMarkers) {
                    distanceMarkersGroup.value.addTo(leafletMapInstance);
                }
            }
        }, 300); // Throttle to maximum once every 300ms

        // Manual center on runner function
        const centerOnRunner = () => {
            if (props.latestPosition && leafletMapInstance) {
                const { latitude, longitude } = props.latestPosition;
                leafletMapInstance.setView([latitude, longitude], 17);
                showNoLocationBanner.value = false;
                // Reset user interaction flag since this is manual centering
                userHasInteracted = false;
                lastCenterTime = Date.now();
            }
        };

        return { 
            map, 
            showNoLocationBanner, 
            showSettings, 
            mapSettings,
            toggleSettings,
            updateMapSettings,
            centerOnRunner
        };
    }
};
</script>
<style scoped>
html, body, .map-view, #map {
    height: 100%;
    width: 100%;
    margin: 0;
    padding: 0;
}

.map-view {
    position: fixed;
    top: 0;
    left: 0;
}

/* Desktop layout adjustments - map should not extend under sidebar */
@media (min-width: 1024px) {
    .map-view {
        right: 400px; /* Don't extend under the 400px sidebar */
    }
}

/* No location banner styles */
.no-location-banner {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1000;
    background: rgba(52, 152, 219, 0.95);
    color: white;
    padding: 12px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(10px);
    animation: slideDown 0.3s ease-out;
}

.banner-content {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
}

.banner-icon {
    font-size: 16px;
    opacity: 0.9;
}

.banner-text {
    white-space: nowrap;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateX(-50%) translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
}

/* Responsive banner for smaller screens */
@media (max-width: 768px) {
    .no-location-banner {
        left: 10px;
        right: 10px;
        transform: none;
        width: calc(100% - 20px);
    }
    
    .banner-text {
        white-space: normal;
        font-size: 13px;
    }
}

/* Settings button styles */
.settings-btn {
    position: fixed;
    top: 80px;
    right: 20px;
    z-index: 1000;
    background: white;
    border: none;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.settings-btn:hover {
    background: #f8f9fa;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
    transform: scale(1.05);
}

.settings-btn:active {
    transform: scale(0.95);
}

.settings-icon {
    font-size: 20px;
    user-select: none;
}

/* Adjust settings button position for desktop with sidebar */
@media (min-width: 1024px) {
    .settings-btn {
        right: 420px; /* Account for 400px sidebar + 20px margin */
    }
}

/* Mobile responsive settings button */
@media (max-width: 768px) {
    .settings-btn {
        width: 45px;
        height: 45px;
        top: 70px;
        right: 15px;
    }
    
    .settings-icon {
        font-size: 18px;
    }
}

/* Center on runner button */
.center-btn {
    position: fixed;
    bottom: 20px; /* Bottom right corner */
    right: 20px;
    z-index: 1000;
    background: #e74c3c;
    border: none;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    color: white;
}

.center-btn:hover {
    background: #c0392b;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
    transform: scale(1.05);
}

.center-btn:active {
    transform: scale(0.95);
}

.center-icon {
    font-size: 20px;
    user-select: none;
}

/* Adjust center button position for desktop with sidebar */
@media (min-width: 1024px) {
    .center-btn {
        right: 420px; /* Account for 400px sidebar + 20px margin */
    }
}

/* Mobile responsive center button */
@media (max-width: 768px) {
    .center-btn {
        width: 45px;
        height: 45px;
        bottom: 15px; /* Bottom right corner on mobile */
        right: 15px;
    }
    
    .center-icon {
        font-size: 18px;
    }
}
</style>

<style>
/* Global styles for Leaflet markers (not scoped) */
.current-position-marker {
    width: 32px;
    height: 32px;
    background: #e74c3c;
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    animation: pulse 2s infinite;
}

/* User name label for current position marker */
.user-name-label {
    position: absolute;
    top: -35px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(44, 62, 80, 0.9);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: bold;
    white-space: nowrap;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    pointer-events: none;
}

/* Inactive marker styling - gray color with no animation */
.current-position-marker.inactive {
    background: #95a5a6;
    animation: none;
    opacity: 0.8;
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.7);
    }
    70% {
        box-shadow: 0 0 0 10px rgba(231, 76, 60, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(231, 76, 60, 0);
    }
}

.current-position-icon {
    background: transparent !important;
    border: none !important;
    position: relative;
}

.direction-arrow {
    position: absolute;
    top: -5px;
    left: 50%;
    width: 0;
    height: 0;
    border-left: 3px solid transparent;
    border-right: 3px solid transparent;
    border-bottom: 6px solid #e74c3c;
    transform-origin: 50% calc(100% + 5px);
    margin-left: -3px;
    z-index: 1000;
    opacity: 0.9;
}

/* Inactive direction arrow styling */
.direction-arrow.inactive {
    border-bottom-color: #95a5a6;
    opacity: 0.6;
}

.position-popup {
    font-size: 13px;
    line-height: 1.4;
    min-width: 150px;
}

.position-popup strong {
    color: #2c3e50;
    display: block;
    margin-bottom: 8px;
}

/* Style the track polyline popup */
.leaflet-popup-content-wrapper {
    border-radius: 8px;
    box-shadow: 0 3px 14px rgba(0, 0, 0, 0.4);
}

.leaflet-popup-tip {
    box-shadow: none;
}

/* Event marker styles */
.event-marker {
    width: 20px;
    height: 20px;
    border: 2px solid white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.event-marker-icon {
    background: transparent !important;
    border: none !important;
}

.event-marker-popup {
    font-size: 13px;
    line-height: 1.4;
    min-width: 150px;
}

.event-marker-popup strong {
    color: #2c3e50;
    display: block;
    margin-bottom: 6px;
}

/* Distance marker styles */
.distance-marker-container {
    background: transparent !important;
    border: none !important;
}

.distance-marker {
    background: #3498db;
    color: white;
    padding: 2px 6px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: bold;
    text-align: center;
    min-width: 20px;
    border: 1px solid white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}
</style>
